import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Login = () => {
  const { t, tx, lang, toggleLang, companies, setCompanyId, setAuthed } = useApp();
  const [step, setStep] = useState('login');
  const [email, setEmail] = useState('xavi@novatech.com');
  const [password, setPassword] = useState('demo1234');
  const [selected, setSelected] = useState(companies[0].id);
  const navigate = useNavigate();

  const enter = () => {
    setCompanyId(selected);
    setAuthed(true);
    navigate('/app/today');
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-6 dark" data-theme="dark">
      <div
        className="pointer-events-none absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/finpulse-bg.jpg)` }}
      />
      <div className="pointer-events-none absolute inset-0 bg-[#12162a]/70" />

      <button
        onClick={toggleLang}
        data-testid="login-lang-toggle"
        className="absolute top-5 right-5 z-10 text-[11px] font-medium tracking-wide px-2.5 h-9 rounded-lg text-white/70 bg-white/10 hover:bg-white/15 hover:text-white transition-colors"
      >
        <span className={lang === 'es' ? 'text-white' : ''}>ES</span>
        <span className="mx-1.5 text-white/30">/</span>
        <span className={lang === 'en' ? 'text-white' : ''}>EN</span>
      </button>

      <div className="relative z-10 w-full max-w-[380px]">
        <div className="mb-8 text-center">
          <p className="font-heading font-extrabold text-2xl tracking-[0.08em] text-white">FINPULSE</p>
        </div>

        <div className="rounded-2xl bg-white px-7 py-8 shadow-[0_20px_60px_rgba(0,0,0,0.25)]">
          <AnimatePresence mode="wait">
            {step === 'login' ? (
              <motion.div
                key="login"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2 }}
              >
                <h1 className="font-heading text-xl font-semibold text-zinc-900 tracking-tight">{t('login.signin')}</h1>
                <p className="mt-1.5 text-sm text-zinc-500 leading-relaxed">{t('login.subtitle')}</p>

                <div className="mt-7 space-y-4">
                  <div>
                    <label className="block text-[11px] font-medium text-zinc-500 tracking-wide mb-1.5">{t('login.email')}</label>
                    <input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="login-email"
                      className="w-full h-11 px-3.5 rounded-lg border border-zinc-200 bg-white text-sm text-zinc-900 outline-none focus:border-zinc-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-zinc-500 tracking-wide mb-1.5">{t('login.password')}</label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="login-password"
                      className="w-full h-11 px-3.5 rounded-lg border border-zinc-200 bg-white text-sm text-zinc-900 outline-none focus:border-zinc-400 transition-colors"
                    />
                  </div>
                  <button
                    onClick={() => setStep('company')}
                    data-testid="login-submit"
                    className="w-full h-11 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium transition-colors"
                  >
                    {t('login.signin')}
                  </button>
                  <button
                    onClick={() => setStep('company')}
                    data-testid="login-demo"
                    className="w-full h-10 text-sm text-zinc-500 hover:text-zinc-800 transition-colors"
                  >
                    {t('login.demo')}
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="company"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2 }}
              >
                <h1 className="font-heading text-xl font-semibold text-zinc-900 tracking-tight">{t('login.selectCompany')}</h1>
                <p className="mt-1.5 text-sm text-zinc-500 leading-relaxed">{t('login.selectCompanyDesc')}</p>

                <div className="mt-6 space-y-2">
                  {companies.map((c) => {
                    const active = selected === c.id;
                    return (
                      <button
                        key={c.id}
                        onClick={() => setSelected(c.id)}
                        data-testid={`select-company-${c.id}`}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                          active ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'
                        }`}
                      >
                        <div
                          className="w-9 h-9 rounded-md flex items-center justify-center text-xs font-bold text-white shrink-0"
                          style={{ background: c.color }}
                        >
                          {c.logo}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-zinc-900 text-sm truncate">{c.name}</p>
                          <p className="text-xs text-zinc-500 truncate">{tx(c.sector)}</p>
                        </div>
                        {active && (
                          <div className="w-5 h-5 rounded-full bg-zinc-900 flex items-center justify-center shrink-0">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={enter}
                  data-testid="enter-app"
                  className="mt-6 w-full h-11 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium transition-colors"
                >
                  {t('login.enter')}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Login;
